import { SkeletonView } from '@/design-system/components/cards';
import { Icon } from '@/design-system/components/icons/Icon';
import { Space } from '@/design-system/components/Space';
import { H3, TextAmount, TextBody } from '@/design-system/components/typography/UniTypography';
import { formatAmountByDecimals } from '@/utils/numberFormatting';
import { Pressable, SxProp, View } from 'dripsy';
import { t } from 'i18next';
import React, { useContext } from 'react';
import { useStore } from 'store/store';

interface NetAssetValueProps {
  netAssetValue: string;
  decimals?: number;
  sx?: SxProp;
  loading?: boolean;
  showAmount?: boolean;
  onChangeShowAmount?: (value: boolean) => void;
}

export const NetAssetValue: React.FC<NetAssetValueProps> = ({
  netAssetValue,
  sx,
  decimals = 2,
  loading,
  showAmount,
  onChangeShowAmount
}) => {
  const store = useStore();

  let isShowAmount = showAmount;
  const onChange = (value: boolean) => {
    if (onChangeShowAmount) {
      onChangeShowAmount(value);
    } else {
      store.changeShowAmount(value);
    }
  };

  if (showAmount === undefined) {
    isShowAmount = store.showAmount;
  }

  const baseCurrency = t(`earn-invest::glossary.net-value-${store.baseCurrency.toLowerCase()}`);

  return (
    <View sx={{ ...styles.container, ...sx }}>
      <View sx={styles.label}>
        <TextBody semiBold>{baseCurrency}</TextBody>
        <Space width="$2" />
        <Pressable onPress={() => onChange(!showAmount)}>
          <Icon name={isShowAmount ? 'show' : 'hide'} />
        </Pressable>
      </View>
      <Space height="$1" />
      {loading && <SkeletonView height={39} width={230} />}
      {!loading && (
        <H3 bold sx={{ color: '$textThemeSecondary' }}>
          {isShowAmount ? formatAmountByDecimals({ value: netAssetValue, decimals, suffix: store.baseCurrency }) : '************'}
        </H3>
      )}
    </View>
  );
};

enum StyleName {
  container = 'container',
  label = 'label'
}

const styles: Record<StyleName, SxProp> = {
  container: {},
  label: {
    flexDirection: 'row',
    alignItems: 'flex-end'
  }
};
